import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { SparklesIcon, PlusIcon, SearchIcon, LayoutGridIcon, ListIcon, AlertCircleIcon, MoreVerticalIcon, ArrowLeftIcon, UploadCloudIcon, CalendarPlusIcon, EyeOffIcon, EyeIcon, FolderIcon, SlidersHorizontalIcon } from 'lucide-react';
import { useBusiness, CATALOG_LABEL } from './BusinessContext';
import { PremiumSelect } from './PremiumSelect';
import { CatalogItemModal } from './CatalogItemModal';
import { CatalogChat } from './CatalogChat';
import { UpgradePlanModal } from './UpgradePlanModal';
import { CatalogInstructionsModal } from './CatalogInstructionsModal';
export function BusinessCatalog() {
  const {
    hub,
    canAddListing,
    currentPlanId,
    listingCount,
    publishListings,
    unpublishListings,
    PACKAGES
  } = useBusiness();
  const catalogName = CATALOG_LABEL[hub?.type ?? 'generic'] || 'Catalog';
  // Toggle for demo purposes
  const [isPopulated, setIsPopulated] = useState(false);
  const [showChat, setShowChat] = useState(false);
  const [showUpgrade, setShowUpgrade] = useState(false);
  const [showInstructions, setShowInstructions] = useState(false);
  const [selectedItem, setSelectedItem] = useState<string | null>(null);
  const currentPlan = PACKAGES.find(p => p.id === currentPlanId) || PACKAGES[0];
  const limitLabel = currentPlan.listingLimit === Infinity ? 'Unlimited' : currentPlan.listingLimit;
  const [viewMode, setViewMode] = useState<'card' | 'table' | 'ai'>('card');
  const [activeFilter, setActiveFilter] = useState('All');
  const [activeCategory, setActiveCategory] = useState('All Categories');
  const [talabFilter, setTalabFilter] = useState<'all' | 'talab' | 'inventory'>('talab');
  const filters = ['All', 'Active', 'Draft', 'Needs AI Rules'];
  const categories = ['All Categories', 'SUVs', 'Sedans', 'Hatchbacks'];
  const [items, setItems] = useState([{
    id: '1',
    title: 'Toyota Prado GXR 2018',
    category: 'SUVs',
    price: 'AED 350/day',
    status: 'Available',
    talabPublished: true,
    aiScore: 92,
    views: 1240,
    rules: 'Delivery configured',
    leads: 18,
    validityDays: 12,
    totalValidity: 30,
    image: 'https://images.unsplash.com/photo-1550133730-695473e544be?auto=format&fit=crop&q=80&w=400'
  }, {
    id: '2',
    title: 'Nissan Patrol 2020',
    category: 'SUVs',
    price: 'AED 450/day',
    status: 'Available',
    talabPublished: true,
    aiScore: 76,
    views: 2180,
    rules: 'Missing airport pickup rule',
    leads: 24,
    validityDays: 5,
    totalValidity: 30,
    alert: true,
    image: 'https://images.unsplash.com/photo-1533473359331-0135ef1b58bf?auto=format&fit=crop&q=80&w=400'
  }, {
    id: '3',
    title: 'Toyota Yaris 2022',
    category: 'Hatchbacks',
    price: 'AED 120/day',
    status: 'Inventory',
    talabPublished: false,
    aiScore: 41,
    views: 0,
    rules: 'Needs pricing rules',
    leads: 0,
    validityDays: 30,
    totalValidity: 30,
    alert: true,
    image: 'https://images.unsplash.com/photo-1590362891991-f776e747a588?auto=format&fit=crop&q=80&w=400'
  }]);
  const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set());
  const filteredItems = items.filter(item => activeCategory === 'All Categories' || item.category === activeCategory).filter(item => talabFilter === 'all' || talabFilter === 'talab' && item.talabPublished || talabFilter === 'inventory' && !item.talabPublished);
  const offTalabItems = filteredItems.filter(item => !item.talabPublished);
  const onTalabItems = filteredItems.filter(item => item.talabPublished);
  // Breakdown of the current selection by Talab state (drives action-bar buttons).
  const selectedOffTalab = items.filter(item => selectedItems.has(item.id) && !item.talabPublished);
  const selectedOnTalab = items.filter(item => selectedItems.has(item.id) && item.talabPublished);
  const allOffTalabSelected = offTalabItems.length > 0 && offTalabItems.every(item => selectedItems.has(item.id));
  const allOnTalabSelected = onTalabItems.length > 0 && onTalabItems.every(item => selectedItems.has(item.id));
  const toggleSelectAllOffTalab = () => {
    const newSelected = new Set(selectedItems);
    if (allOffTalabSelected) {
      offTalabItems.forEach(item => newSelected.delete(item.id));
    } else {
      offTalabItems.forEach(item => newSelected.add(item.id));
    }
    setSelectedItems(newSelected);
  };
  const toggleSelectAllOnTalab = () => {
    const newSelected = new Set(selectedItems);
    if (allOnTalabSelected) {
      onTalabItems.forEach(item => newSelected.delete(item.id));
    } else {
      onTalabItems.forEach(item => newSelected.add(item.id));
    }
    setSelectedItems(newSelected);
  };
  const toggleSelectItem = (id: string) => {
    const newSelected = new Set(selectedItems);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedItems(newSelected);
  };
  const handlePublishSelected = () => {
    const ids = selectedOffTalab.map(item => item.id);
    if (ids.length === 0) return;
    if (listingCount + ids.length > currentPlan.listingLimit) {
      setShowUpgrade(true);
      return;
    }
    setItems(prev => prev.map(item => ids.includes(item.id) ? {
      ...item,
      talabPublished: true,
      validityDays: 30,
      totalValidity: 30
    } : item));
    publishListings(ids.length);
    setSelectedItems(new Set());
  };
  const handleUnpublishSelected = () => {
    const ids = selectedOnTalab.map(item => item.id);
    if (ids.length === 0) return;
    setItems(prev => prev.map(item => ids.includes(item.id) ? {
      ...item,
      talabPublished: false,
      status: 'Inventory'
    } : item));
    unpublishListings(ids.length);
    setSelectedItems(new Set());
  };
  const handleExtendSelected = () => {
    const ids = selectedOnTalab.map(item => item.id);
    if (ids.length === 0) return;
    setItems(prev => prev.map(item => ids.includes(item.id) ? {
      ...item,
      validityDays: item.validityDays + 30,
      totalValidity: Math.max(item.totalValidity, item.validityDays + 30)
    } : item));
    setSelectedItems(new Set());
  };
  const handleChatComplete = () => {
    setIsPopulated(true);
    setShowChat(false);
  };
  const handleAddInventory = () => {
    if (!canAddListing) {
      setShowUpgrade(true);
    } else {
      setShowChat(true);
    }
  };
  return <div className="w-full max-w-6xl mx-auto h-full flex flex-col" data-id="element-395">
      {/* Demo Toggle */}
      <div className="flex justify-end mb-4 flex-shrink-0" data-id="element-396">
        <button onClick={() => {
        setIsPopulated(!isPopulated);
        setShowChat(false);
      }} className="text-xs font-medium text-slate-400 hover:text-slate-600 bg-white px-3 py-1.5 rounded-full border border-slate-200 shadow-sm transition-colors" data-id="element-397">
          Toggle Demo State (Currently: {isPopulated ? 'Populated' : 'Empty'})
        </button>
      </div>

      <AnimatePresence mode="wait" data-id="element-398">
        {!isPopulated || showChat ? <motion.div key="chat" initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -10
      }} className="w-full flex-1 min-h-0 flex flex-col" data-id="element-399">
            {isPopulated && showChat && <button onClick={() => setShowChat(false)} className="mb-4 flex items-center gap-2 text-sm font-medium text-slate-500 hover:text-primary-indigo transition-colors" data-id="element-400">
                <ArrowLeftIcon className="w-4 h-4" data-id="element-401" /> Back to Catalog
              </button>}
            <CatalogChat catalogName={catalogName} onPopulateDemo={handleChatComplete} data-id="element-402" />
          </motion.div> : <motion.div key="populated" initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -10
      }} className="space-y-8 pb-24" data-id="element-403">
            {/* Header & Actions */}
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-4" data-id="element-404">
              <div data-id="element-405">
                <div className="flex items-center gap-3 mb-2" data-id="element-406">
                  <h2 className="text-3xl font-display font-bold text-dark-navy" data-id="element-407">
                    {catalogName}
                  </h2>
                  <div className="flex items-center gap-2 px-3 py-1 bg-slate-100 rounded-full text-xs font-medium text-slate-600" data-id="element-408">
                    <span data-id="element-409">{currentPlan.name} Plan</span>
                    <span className="w-1 h-1 rounded-full bg-slate-300" data-id="element-410" />
                    <span data-id="element-411">
                      {listingCount} / {limitLabel} listings
                    </span>
                    {!canAddListing && <button onClick={() => setShowUpgrade(true)} className="ml-1 text-primary-indigo hover:text-indigo-700 font-bold" data-id="element-412">
                        Upgrade
                      </button>}
                  </div>
                </div>
                <p className="text-lg text-slate-500" data-id="element-413">
                  Manage your inventory, pricing, and AI rules.
                </p>
              </div>
              <div className="flex items-center gap-2.5" data-id="element-414">
                <button onClick={() => setShowInstructions(true)} className="px-4 py-2.5 bg-white border border-slate-200 text-slate-700 font-medium rounded-xl shadow-sm hover:border-ai-purple hover:text-ai-purple hover:shadow transition-all flex items-center gap-2" data-id="element-415">
                  <SparklesIcon className="w-5 h-5 text-ai-purple" data-id="element-416" />
                  <span className="hidden sm:inline" data-id="element-417">
                    Catalogue Instructions
                  </span>
                  <span className="sm:hidden" data-id="element-418">Instructions</span>
                </button>
                <button onClick={handleAddInventory} className="px-5 py-2.5 bg-primary-indigo text-white font-medium rounded-xl shadow-sm hover:bg-indigo-700 hover:shadow transition-all flex items-center gap-2" data-id="element-419">
                  <PlusIcon className="w-5 h-5" data-id="element-420" /> Add Inventory
                </button>
              </div>
            </div>

            {/* Curated KPIs */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-421">
              <KpiCard title="Total Items" value="42" data-id="element-422" />
              <KpiCard title="Active Listings" value="38" trend="+2 this week" data-id="element-423" />
              <KpiCard title="Needs AI Rules" value="7" alert onClick={() => setViewMode('ai')} data-id="element-424" />
            </div>

            {/* Talab publication segmented control */}
            <div className="flex items-center gap-2 flex-wrap" data-id="element-425">
              {([{
            id: 'talab',
            label: 'Published on Talab'
          }, {
            id: 'inventory',
            label: 'Inventory (Off Talab)'
          }] as const).map(seg => <button key={seg.id} onClick={() => setTalabFilter(seg.id)} className={`px-4 py-2 rounded-full text-sm font-medium border transition-colors ${talabFilter === seg.id ? 'bg-primary-indigo text-white border-primary-indigo shadow-sm' : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300'}`} data-id="element-426">
                  {seg.label}
                </button>)}
              <span className="text-xs text-slate-400 ml-1 hidden lg:inline" data-id="element-427">
                Inventory items stay live on your website widget even when off
                Talab.
              </span>
            </div>

            {/* Unified Toolbar */}
            <div className="bg-white p-3 rounded-2xl border border-slate-200/60 shadow-sm flex flex-col md:flex-row md:items-center justify-between gap-4" data-id="element-428">
              <div className="flex items-center gap-2 flex-wrap" data-id="element-429">
                {offTalabItems.length > 0 && <label className="flex items-center gap-2 px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm font-medium text-slate-600 cursor-pointer hover:bg-slate-100 transition-colors" data-id="element-430">
                    <input type="checkbox" checked={allOffTalabSelected} onChange={toggleSelectAllOffTalab} className="w-4 h-4 rounded border-slate-300 text-primary-indigo focus:ring-primary-indigo" data-id="element-431" />
                    Select all off Talab
                  </label>}
                {onTalabItems.length > 0 && <label className="flex items-center gap-2 px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm font-medium text-slate-600 cursor-pointer hover:bg-slate-100 transition-colors" data-id="element-432">
                    <input type="checkbox" checked={allOnTalabSelected} onChange={toggleSelectAllOnTalab} className="w-4 h-4 rounded border-slate-300 text-primary-indigo focus:ring-primary-indigo" data-id="element-433" />
                    Select all on Talab
                  </label>}
                <div className="relative" data-id="element-434">
                  <SearchIcon className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" data-id="element-435" />
                  <input type="text" placeholder="Search inventory..." className="w-full md:w-64 pl-9 pr-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary-indigo/20 focus:border-primary-indigo transition-all" data-id="element-436" />
                </div>
                <PremiumSelect compact icon={FolderIcon} value={activeCategory} options={categories} onChange={setActiveCategory} className="min-w-[10rem]" data-id="element-437" />
                <PremiumSelect compact icon={SlidersHorizontalIcon} value={activeFilter} options={filters} onChange={setActiveFilter} className="min-w-[9rem]" data-id="element-438" />
              </div>

              <div className="flex bg-slate-100/80 p-1 rounded-xl" data-id="element-439">
                <button onClick={() => setViewMode('card')} className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${viewMode === 'card' ? 'bg-white shadow-sm text-primary-indigo' : 'text-slate-500 hover:text-slate-700'}`} data-id="element-440">
                  <LayoutGridIcon className="w-4 h-4" data-id="element-441" />{' '}
                  <span className="hidden sm:inline" data-id="element-442">Cards</span>
                </button>
                <button onClick={() => setViewMode('table')} className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${viewMode === 'table' ? 'bg-white shadow-sm text-primary-indigo' : 'text-slate-500 hover:text-slate-700'}`} data-id="element-443">
                  <ListIcon className="w-4 h-4" data-id="element-444" />{' '}
                  <span className="hidden sm:inline" data-id="element-445">List</span>
                </button>
              </div>
            </div>

            {/* Views */}
            {viewMode === 'card' && <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" data-id="element-446">
                {filteredItems.map(item => <CatalogItemCard key={item.id} {...item} selected={selectedItems.has(item.id)} onSelect={() => toggleSelectItem(item.id)} onClick={() => setSelectedItem(item.title)} data-id="element-447" />)}
              </div>}

            {viewMode === 'table' && <div className="bg-white border border-slate-200/60 rounded-2xl overflow-hidden shadow-sm" data-id="element-448">
                <table className="w-full text-left text-sm" data-id="element-449">
                  <thead className="bg-slate-50 border-b border-slate-100" data-id="element-450">
                    <tr data-id="element-451">
                      <th className="px-6 py-4 font-medium text-slate-500" data-id="element-452">
                        Item
                      </th>
                      <th className="px-6 py-4 font-medium text-slate-500" data-id="element-453">
                        Category
                      </th>
                      <th className="px-6 py-4 font-medium text-slate-500" data-id="element-454">
                        Price
                      </th>
                      <th className="px-6 py-4 font-medium text-slate-500" data-id="element-455">
                        Status
                      </th>
                      <th className="px-6 py-4 font-medium text-slate-500" data-id="element-456">
                        Talab
                      </th>
                      <th className="px-6 py-4 font-medium text-slate-500" data-id="element-457">
                        AI Readiness
                      </th>
                      <th className="px-6 py-4 font-medium text-slate-500" data-id="element-458">
                        Leads
                      </th>
                      <th className="px-6 py-4 font-medium text-slate-500" data-id="element-459"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100" data-id="element-460">
                    {filteredItems.map(item => <TableRow key={item.id} {...item} selected={selectedItems.has(item.id)} onSelect={() => toggleSelectItem(item.id)} onClick={() => setSelectedItem(item.title)} data-id="element-461" />)}
                  </tbody>
                </table>
              </div>}

            {viewMode === 'ai' && <div className="space-y-6" data-id="element-462">
                <div className="bg-ai-purple/5 border border-ai-purple/10 rounded-2xl p-6 flex items-start gap-4" data-id="element-463">
                  <div className="w-12 h-12 rounded-xl bg-white flex items-center justify-center shadow-sm flex-shrink-0" data-id="element-464">
                    <SparklesIcon className="w-6 h-6 text-ai-purple" data-id="element-465" />
                  </div>
                  <div data-id="element-466">
                    <h4 className="text-lg font-bold text-dark-navy mb-1" data-id="element-467">
                      AI Review Mode
                    </h4>
                    <p className="text-slate-600" data-id="element-468">
                      Talab AI has identified items that need your attention to
                      perform better in search and chat.
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-469">
                  <AIReviewGroup title="Missing Delivery Rules" count={3} items={['Nissan Patrol 2020', 'Range Rover Sport 2021', 'Mercedes G63 2022']} action="Add Rule" data-id="element-470" />
                  <AIReviewGroup title="Weak Descriptions" count={5} items={['Toyota Yaris 2022', 'Kia Picanto 2021', 'Hyundai Tucson 2023']} action="Generate with AI" data-id="element-471" />
                </div>
              </div>}
          </motion.div>}
      </AnimatePresence>

      {/* Item detail */}
      {selectedItem && <CatalogItemModal itemTitle={selectedItem} onClose={() => setSelectedItem(null)} data-id="element-472" />}

      {showInstructions && <CatalogInstructionsModal onClose={() => setShowInstructions(false)} data-id="element-473" />}

      {showUpgrade && <UpgradePlanModal onClose={() => setShowUpgrade(false)} data-id="element-474" />}

      <AnimatePresence data-id="element-475">
        {selectedItems.size > 0 && <motion.div initial={{
        opacity: 0,
        y: 50
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: 50
      }} className="fixed bottom-8 left-1/2 -translate-x-1/2 bg-dark-navy text-white px-6 py-4 rounded-2xl shadow-2xl flex items-center gap-6 z-50" data-id="element-476">
            <div className="flex items-center gap-2 font-medium" data-id="element-477">
              <span className="w-6 h-6 bg-white/20 rounded-full flex items-center justify-center text-sm" data-id="element-478">
                {selectedItems.size}
              </span>
              selected
            </div>
            <div className="w-px h-6 bg-white/20" data-id="element-479" />
            <div className="flex items-center gap-2 flex-wrap" data-id="element-480">
              <button onClick={() => setSelectedItems(new Set())} className="px-4 py-2 text-sm font-medium text-slate-300 hover:text-white transition-colors" data-id="element-481">
                Clear
              </button>
              {selectedOnTalab.length > 0 && <>
                  <button onClick={handleExtendSelected} className="px-4 py-2 bg-white/10 hover:bg-white/20 text-white text-sm font-bold rounded-xl transition-colors flex items-center gap-2" data-id="element-482">
                    <CalendarPlusIcon className="w-4 h-4" data-id="element-483" /> Extend 30 days
                  </button>
                  <button onClick={handleUnpublishSelected} className="px-4 py-2 bg-white/10 hover:bg-red-500/80 text-white text-sm font-bold rounded-xl transition-colors flex items-center gap-2" data-id="element-484">
                    <EyeOffIcon className="w-4 h-4" data-id="element-485" /> Unpublish
                  </button>
                </>}
              {selectedOffTalab.length > 0 && <button onClick={handlePublishSelected} className="px-5 py-2 bg-primary-indigo hover:bg-indigo-500 text-white text-sm font-bold rounded-xl transition-colors shadow-sm flex items-center gap-2" data-id="element-486">
                  <UploadCloudIcon className="w-4 h-4" data-id="element-487" /> Publish to Talab
                </button>}
            </div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}
function KpiCard({
  title,
  value,
  trend,
  alert,
  onClick
}: any) {
  const Card = onClick ? 'button' : 'div';
  return <Card onClick={onClick} className={`p-6 bg-white border rounded-2xl flex flex-col ${alert ? 'border-amber-200 bg-amber-50/30' : 'border-slate-200/60 shadow-sm'} ${onClick ? 'hover:shadow-md hover:border-primary-indigo/30 transition-all text-left' : ''}`} data-id="element-488">
      <span className="text-sm font-medium text-slate-500 mb-2" data-id="element-489">{title}</span>
      <div className="flex items-end gap-3" data-id="element-490">
        <span className={`text-3xl font-display font-bold leading-none ${alert ? 'text-amber-600' : 'text-dark-navy'}`} data-id="element-491">
          {value}
        </span>
        {trend && <span className="text-sm font-medium text-primary-indigo mb-0.5 bg-indigo-50 px-2 py-0.5 rounded-md" data-id="element-492">
            {trend}
          </span>}
      </div>
    </Card>;
}
function formatValidUntil(days: number) {
  const date = new Date();
  date.setDate(date.getDate() + days);
  return date.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric'
  });
}
function CatalogItemCard({
  title,
  category,
  price,
  status,
  talabPublished = true,
  aiScore,
  views = 0,
  rules,
  leads,
  image,
  validityDays = 30,
  totalValidity = 30,
  alert,
  selected,
  onSelect,
  onClick
}: any) {
  return <div className={`bg-white border rounded-2xl overflow-hidden shadow-sm hover:shadow-md transition-all flex flex-col group cursor-pointer relative ${selected ? 'border-primary-indigo ring-1 ring-primary-indigo' : 'border-slate-200/60 hover:border-primary-indigo/30'}`} onClick={onClick} data-id="element-493">
      <div className={`absolute top-2.5 left-2.5 z-10 transition-opacity ${selected ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`} onClick={e => e.stopPropagation()} data-id="element-494">
        <input type="checkbox" checked={selected} onChange={onSelect} className="w-5 h-5 rounded border-slate-300 text-primary-indigo focus:ring-primary-indigo bg-white/90 backdrop-blur-sm shadow-sm cursor-pointer" data-id="element-495" />
      </div>
      <div className="h-32 bg-slate-100 relative overflow-hidden" data-id="element-496">
        <img src={image} alt={title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" data-id="element-497" />
        <div className="absolute top-2.5 right-2.5 px-2 py-0.5 bg-white/95 backdrop-blur-sm rounded-md text-[11px] font-bold text-dark-navy shadow-sm" data-id="element-498">
          {status}
        </div>
        {category && <div className="absolute top-2.5 left-10 px-2 py-0.5 bg-white/95 backdrop-blur-sm rounded-md text-[11px] font-bold text-primary-indigo shadow-sm transition-all" data-id="element-499">
            {category}
          </div>}
        <div className={`absolute bottom-2.5 left-2.5 px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wide shadow-sm ${talabPublished ? 'bg-green-600 text-white' : 'bg-slate-700 text-white'}`} data-id="element-500">
          {talabPublished ? 'On Talab' : 'Widget only'}
        </div>
      </div>
      <div className="p-4 flex-1 flex flex-col" data-id="element-501">
        <h4 className="font-bold text-dark-navy text-sm mb-0.5 truncate" data-id="element-502">
          {title}
        </h4>
        <div className="text-primary-indigo font-bold text-sm mb-3" data-id="element-503">
          {price}
        </div>

        <div className="space-y-1.5 mb-3 flex-1" data-id="element-504">
          <div className="flex items-center justify-between text-[13px]" data-id="element-505">
            <span className="text-slate-500 flex items-center gap-1.5" data-id="element-506">
              <EyeIcon className="w-3.5 h-3.5 text-slate-400" data-id="element-507" /> Views
            </span>
            <span className="font-bold text-dark-navy" data-id="element-508">
              {views.toLocaleString()}
            </span>
          </div>
          <div className="flex items-center justify-between text-[13px]" data-id="element-509">
            <span className="text-slate-500" data-id="element-510">Valid till</span>
            <span className={`text-xs font-medium ${talabPublished ? validityDays < 7 ? 'text-amber-600' : 'text-slate-700' : 'text-slate-400'}`} data-id="element-511">
              {talabPublished ? formatValidUntil(validityDays) : 'Not published'}
            </span>
          </div>
          <div className="flex items-center justify-between text-[13px]" data-id="element-512">
            <span className="text-slate-500" data-id="element-513">Leads</span>
            <span className="text-dark-navy font-bold" data-id="element-514">{leads}</span>
          </div>
        </div>

        <div className="pt-2.5 border-t border-slate-100" data-id="element-515">
          {talabPublished ? <>
              <div className="flex items-center justify-between text-xs mb-1.5" data-id="element-516">
                <span className="text-slate-500" data-id="element-517">Talab validity</span>
                <span className={`font-medium ${validityDays < 7 ? 'text-amber-600' : 'text-slate-700'}`} data-id="element-518">
                  {validityDays} days left
                </span>
              </div>
              <div className="w-full h-1.5 bg-slate-100 rounded-full overflow-hidden" data-id="element-519">
                <div className={`h-full transition-all ${validityDays < 7 ? 'bg-amber-500' : 'bg-primary-indigo'}`} style={{
              width: `${validityDays / totalValidity * 100}%`
            }} data-id="element-520" />
              </div>
            </> : <div className="flex items-center justify-between text-xs" data-id="element-521">
              <span className="text-slate-500" data-id="element-522">Status</span>
              <span className="font-medium text-slate-700" data-id="element-523">
                In inventory · live on widget
              </span>
            </div>}
        </div>
      </div>
    </div>;
}
function TableRow({
  title,
  category,
  price,
  status,
  talabPublished = true,
  aiScore,
  leads,
  alert,
  selected,
  onSelect,
  onClick
}: any) {
  return <tr className={`hover:bg-slate-50 transition-colors group cursor-pointer ${selected ? 'bg-indigo-50/50' : ''}`} onClick={onClick} data-id="element-524">
      <td className="px-6 py-4" data-id="element-525">
        <div className="flex items-center gap-4" data-id="element-526">
          <div onClick={e => e.stopPropagation()} data-id="element-527">
            <input type="checkbox" checked={selected} onChange={onSelect} className="w-4 h-4 rounded border-slate-300 text-primary-indigo focus:ring-primary-indigo cursor-pointer" data-id="element-528" />
          </div>
          <div className="w-12 h-12 bg-slate-100 rounded-xl overflow-hidden flex-shrink-0" data-id="element-529">
            <img src="https://images.unsplash.com/photo-1550133730-695473e544be?auto=format&fit=crop&q=80&w=100" alt="" className="w-full h-full object-cover" data-id="element-530" />
          </div>
          <span className="font-bold text-dark-navy" data-id="element-531">{title}</span>
        </div>
      </td>
      <td className="px-6 py-4" data-id="element-532">
        <span className="inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-medium bg-slate-100 text-slate-700" data-id="element-533">
          {category}
        </span>
      </td>
      <td className="px-6 py-4 text-primary-indigo font-bold" data-id="element-534">{price}</td>
      <td className="px-6 py-4" data-id="element-535">
        <span className={`px-2.5 py-1 text-xs font-bold rounded-lg ${status === 'Available' ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-600'}`} data-id="element-536">
          {status}
        </span>
      </td>
      <td className="px-6 py-4" data-id="element-537">
        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-bold rounded-lg ${talabPublished ? 'bg-green-50 text-green-700' : 'bg-slate-100 text-slate-600'}`} data-id="element-538">
          <span className={`w-1.5 h-1.5 rounded-full ${talabPublished ? 'bg-green-500' : 'bg-slate-400'}`} data-id="element-539" />
          {talabPublished ? 'On Talab' : 'Widget only'}
        </span>
      </td>
      <td className="px-6 py-4" data-id="element-540">
        <div className="flex items-center gap-3" data-id="element-541">
          <div className="w-20 h-1.5 bg-slate-100 rounded-full overflow-hidden" data-id="element-542">
            <div className={`h-full ${aiScore > 80 ? 'bg-green-500' : 'bg-amber-500'}`} style={{
            width: `${aiScore}%`
          }} data-id="element-543" />
          </div>
          <span className="text-xs font-bold text-slate-700" data-id="element-544">{aiScore}%</span>
        </div>
      </td>
      <td className="px-6 py-4 text-slate-700 font-medium" data-id="element-545">{leads}</td>
      <td className="px-6 py-4 text-right" data-id="element-546">
        <button className="p-2 text-slate-400 hover:text-primary-indigo rounded-lg hover:bg-indigo-50 opacity-0 group-hover:opacity-100 transition-all" data-id="element-547">
          <MoreVerticalIcon className="w-5 h-5" data-id="element-548" />
        </button>
      </td>
    </tr>;
}
function AIReviewGroup({
  title,
  count,
  items,
  action
}: any) {
  return <div className="bg-white border border-slate-200/60 rounded-2xl p-6 shadow-sm hover:shadow-md transition-shadow" data-id="element-549">
      <div className="flex items-center justify-between mb-5" data-id="element-550">
        <div className="flex items-center gap-2.5" data-id="element-551">
          <AlertCircleIcon className="w-5 h-5 text-amber-500" data-id="element-552" />
          <h4 className="font-bold text-dark-navy text-lg" data-id="element-553">{title}</h4>
        </div>
        <span className="px-2.5 py-1 bg-amber-100 text-amber-700 text-xs font-bold rounded-lg" data-id="element-554">
          {count} items
        </span>
      </div>
      <div className="space-y-2 mb-6" data-id="element-555">
        {items.map((item: string, i: number) => <div key={i} className="text-sm font-medium text-slate-700 flex items-center justify-between p-3 bg-slate-50 rounded-xl border border-slate-100" data-id="element-556">
            {item}
            <button className="text-primary-indigo hover:text-indigo-700 text-xs font-bold transition-colors" data-id="element-557">
              Review
            </button>
          </div>)}
      </div>
      <button className="w-full py-2.5 bg-primary-indigo/5 text-primary-indigo text-sm font-bold rounded-xl hover:bg-primary-indigo/10 transition-colors" data-id="element-558">
        {action} for all {count}
      </button>
    </div>;
}